/**
 * `<EmailTemplatePreview/>` + `<EmailTemplatePreviewSurface/>` — B4.5, the
 * email-notification template + its honest in-settings preview (wireframe
 * `V_B45`, "Calm. Specific. One action. — THE EMAIL THAT GOES OUT").
 *
 * Issue #1043 / #725 (Track B net-new, the LAST feature). This is the DESIGN of
 * the mention email Tarii *will* send once email delivery exists — rendered as a
 * faithful 560px email-card PREVIEW inside the notification settings. It is
 * FE-only by deliberate scope:
 *
 *   - `<EmailTemplatePreview/>` is a pure, reusable, presentational renderer of
 *     the template, driven entirely by a typed `EmailTemplateData` prop (no
 *     hooks, no data layer). The same component is what a future server-side
 *     renderer would feed real values into; here we feed it clearly-illustrative
 *     SAMPLE data so it is unmistakably a preview, never a sent message.
 *   - `<EmailTemplatePreviewSurface/>` wraps it for the settings dialog with the
 *     calm, HONEST framing: a "preview" heading + an explicit "email delivery
 *     isn't switched on yet" banner, plus the `change notification settings`
 *     link that routes back to the B4.4 matrix.
 *
 * **No delivery infra** (founder revenue-first gating): no email provider, no
 * SMTP/SES, no send endpoint, no queue, no server-side renderer. The real
 * delivery machinery (server render + send + bounce/unsubscribe) is a separate
 * DEFERRED issue. This file only draws the template + a preview.
 *
 * Gating: rides the SAME `task_notification_prefs` component as the B4.4 matrix
 * (no new toggle — an inert email toggle with no delivery would be dishonest
 * clutter). The parent (`MapSettingsHome`) only mounts the preview when that key
 * is on for the team; off ⇒ no email surface at all.
 *
 * Honesty (trustable pillar — `feedback_brand_pillars` /
 * `feedback_privacy_assurance_for_team_usage`): the surface NEVER implies email
 * sends today. The `no-reply@tarii.app` From line is illustrative (the domain
 * isn't live); the surrounding copy makes the "this is a template, not a live
 * sender" stance explicit. Calm / specific / one-action copy. No tier language;
 * burnout never named; no per-person metric (the email is about one note on one
 * task, never a scorecard about anyone).
 *
 * Editorial only: this is a net-new editorial surface (the whole Map renders
 * editorial today). It reads `useMapTheme()` so the serif/mono/palette stay
 * single-sourced (`feedback_taskmap_clean_code_standards`). Isolation: taskmap-
 * local theme + atoms only — no scheduler import, no data layer.
 */

import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";

/**
 * The typed shape that drives the template — every variable the email
 * interpolates. A future server-side renderer would build this from a real
 * mention event; the preview builds it from `SAMPLE_EMAIL` below.
 */
export interface EmailTemplateData {
  /** Display name of the person who @-mentioned the recipient. */
  actor: string;
  /** Display name of the recipient ("Hi <recipient>,"). */
  recipient: string;
  /** Team name the note lives in. */
  team: string;
  /** Title of the task the note is on (shown as a mono LABEL). */
  taskTitle: string;
  /** The note's body, quoted verbatim. */
  noteQuote: string;
  /** The branch / parent context line ("in: <branch>"). */
  branch: string;
  /** A human time stamp ("today 2:41 PM") — illustrative in the preview. */
  time: string;
  /** Where "Open this task →" would point (illustrative href in the preview). */
  deepLink: string;
}

/**
 * The clearly-illustrative sample the preview renders with — verbatim from the
 * wireframe `V_B45`, so the founder sees exactly the locked design. NOT a real
 * person / team / send; purely a worked example of the template.
 */
export const SAMPLE_EMAIL: EmailTemplateData = {
  actor: "Dr. A. Tan",
  recipient: "B. Kumar",
  team: "Cardio-Imaging Outcomes Study",
  taskTitle: "Synthesise findings",
  noteQuote:
    "@BK — let's make sure we cite the 2024 ESC consensus paper before drawing conclusions.",
  branch: "Lit review",
  time: "today 2:41 PM",
  deepLink: "#",
};

/** The illustrative sender — the domain isn't live yet (kept as the wireframe shows). */
const FROM_ADDRESS = "Tarii <no-reply@tarii.app>";

export interface EmailTemplatePreviewProps {
  /** The values the template interpolates. Defaults to `SAMPLE_EMAIL`. */
  data?: EmailTemplateData;
  /**
   * Called when the footer "change notification settings" link is clicked.
   * In the settings surface this routes back to the B4.4 matrix. Optional so
   * the bare template can be embedded read-only elsewhere (e.g. a docs page).
   */
  onChangeSettings?: () => void;
}

/**
 * The 560px email card itself (wireframe `V_B45`): a From/Subject mono header
 * over a body with the serif "Tarii" wordmark, the "<actor> mentioned you …"
 * line, an ink-left-border quoted-note block (task title · quote · mono
 * "in: <branch> · <time>"), the single "Open this task →" CTA, and the honest
 * italic footer. Pure + reusable — driven entirely by `data`.
 */
export function EmailTemplatePreview({
  data = SAMPLE_EMAIL,
  onChangeSettings,
}: EmailTemplatePreviewProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const subject = `${data.actor} mentioned you on "${data.taskTitle}"`;

  return (
    <div
      data-testid="email-template-preview"
      className="mx-auto w-full"
      style={{ maxWidth: 560, fontFamily: theme.fonts.sans }}
    >
      <div
        className="overflow-hidden"
        style={{
          backgroundColor: c.cardBg,
          border: `1px solid ${c.rule}`,
          borderRadius: theme.radii.lg,
        }}
      >
        {/* Email header — From / Subject, mono, on a recessed surface. */}
        <div
          data-testid="email-template-header"
          className="px-[18px] py-3"
          style={{
            backgroundColor: c.bgRecess,
            borderBottom: `1px solid ${c.rule}`,
            fontFamily: theme.fonts.mono,
            fontSize: 11.5,
            color: c.muted,
          }}
        >
          <div data-testid="email-template-from">
            <strong style={{ color: c.ink }}>From:</strong> {FROM_ADDRESS}
          </div>
          <div data-testid="email-template-subject" className="mt-0.5">
            <strong style={{ color: c.ink }}>Subject:</strong> {subject}
          </div>
        </div>

        {/* Body. */}
        <div className="px-7 pt-7 pb-5">
          {/* Serif Tarii wordmark. */}
          <div
            data-testid="email-template-wordmark"
            className="mb-3.5 text-[22px] italic"
            style={{ fontFamily: theme.fonts.serif, fontWeight: 400, color: c.ink }}
          >
            Tarii
          </div>

          {/* Greeting + the calm "mentioned you" line. */}
          <div className="text-[14px] leading-relaxed" style={{ color: c.slate }}>
            Hi {data.recipient},
            <p className="my-3" data-testid="email-template-actor-line">
              <strong style={{ color: c.ink }}>{data.actor}</strong> mentioned you on a note in the{" "}
              <em>{data.team}</em> team.
            </p>
          </div>

          {/* Quoted-note block — ink left border, task LABEL · quote · mono context. */}
          <div
            data-testid="email-template-quote"
            className="mt-4 px-4 py-3.5"
            style={{
              backgroundColor: c.canvas,
              borderLeft: `3px solid ${c.ink}`,
              borderRadius: `0 ${theme.radii.md}px ${theme.radii.md}px 0`,
            }}
          >
            <span
              data-testid="email-template-task-title"
              className="text-[10px] uppercase"
              style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.1em" }}
            >
              {data.taskTitle}
            </span>
            <div
              data-testid="email-template-note-body"
              className="mt-2 text-[13px] leading-snug"
              style={{ color: c.slate }}
            >
              “{data.noteQuote}”
            </div>
            <div
              data-testid="email-template-context"
              className="mt-2.5 text-[10.5px] uppercase"
              style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.06em" }}
            >
              in: {data.branch} · {data.time}
            </div>
          </div>

          {/* The single action. */}
          <div className="mt-5">
            <a
              data-testid="email-template-cta"
              href={data.deepLink}
              className="inline-flex items-center rounded-md text-[12.5px] font-medium"
              style={{
                backgroundColor: c.ink,
                color: c.canvas,
                padding: "10px 18px",
                fontFamily: theme.fonts.sans,
              }}
            >
              Open this task →
            </a>
          </div>

          {/* Honest footer — why you got it + the settings link. */}
          <div
            data-testid="email-template-footer"
            className="mt-5 text-[11.5px] italic leading-snug"
            style={{ color: c.muted }}
          >
            You’re getting this because you have <em>Mentions</em> on email.{" "}
            <button
              type="button"
              data-testid="email-template-change-settings"
              onClick={onChangeSettings}
              className="italic underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
              style={{ color: c.teal }}
            >
              change notification settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface EmailTemplatePreviewSurfaceProps {
  /**
   * Routes back to the B4.4 notification matrix. Wired to the same dialog the
   * settings page opens, so "change notification settings" closes the preview
   * and opens the matrix.
   */
  onChangeSettings: () => void;
}

/**
 * The in-settings preview SURFACE — the honest frame around the template
 * (wireframe `V_B45`). A mono kicker + serif heading + an explicit
 * "delivery isn't on yet" banner make crystal clear this is the email Tarii
 * *will* send once email delivery is available — it is NOT switched on today.
 * Below it sits the live `<EmailTemplatePreview/>` rendered with sample data.
 */
export function EmailTemplatePreviewSurface({
  onChangeSettings,
}: EmailTemplatePreviewSurfaceProps) {
  const theme = useMapTheme();
  const c = theme.palette;

  return (
    <div data-testid="email-template-surface" style={{ fontFamily: theme.fonts.sans }}>
      {/* Editorial masthead. */}
      <div className="mb-4">
        <span
          className="text-[10px] uppercase"
          style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.1em" }}
        >
          Email preview
        </span>
        <h3
          data-testid="email-template-heading"
          className="mt-1 text-[22px] italic leading-tight"
          style={{ fontFamily: theme.fonts.serif, fontWeight: 400, color: c.ink }}
        >
          The email we’ll send
        </h3>
      </div>

      {/* HONEST banner — delivery is not switched on yet (trustable pillar). */}
      <div
        data-testid="email-template-delivery-note"
        className="mb-5 rounded-md px-4 py-3 text-[12px] leading-snug"
        style={{
          backgroundColor: c.bgRecess,
          color: c.slate,
          border: `1px solid ${c.rule}`,
        }}
      >
        <strong style={{ color: c.ink }}>Email delivery isn’t switched on yet.</strong> This is a
        preview of the email Tarii will send for a mention once the email channel is available — no
        email is sent today. Your in-app nudges are live now; the example below uses sample names.
      </div>

      {/* The template itself, rendered with the illustrative sample. */}
      <EmailTemplatePreview onChangeSettings={onChangeSettings} />

      {/* A second route back to the matrix, in the surface chrome (the footer
          link inside the template also works). */}
      <p className="mt-4 text-[11.5px] italic" style={{ color: c.muted }}>
        Choose which mentions reach you, and where, in{" "}
        <button
          type="button"
          data-testid="email-template-surface-settings-link"
          onClick={onChangeSettings}
          className="italic underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
          style={{ color: c.teal }}
        >
          notification settings
        </button>
        .
      </p>
    </div>
  );
}

/** Re-exported for tests / future docs embeds. */
export type { MapTheme };
